// Component adapted from a demo on the `next` branch of material-ui
// https://github.com/callemall/material-ui/blob/next/docs/site/src/demos/tables/EnhancedTable.js
import React from 'react';
import PropTypes from 'prop-types';
import keycode from 'keycode';
import { map, isFunction, size, get, find, includes, isEqual, orderBy as _orderBy } from 'lodash';

import Checkbox from '@material-ui/core/Checkbox';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import { createStyles, withStyles } from '@material-ui/core/styles';

import CheckBoxIcon from '@material-ui/icons/CheckBox';
import { parseDate } from '@lib/services/datesService';

import EnhancedTableHead from './EnhancedTableHead';
import EnhancedTableRow from './EnhancedTableRow';
import RowLoading from './RowLoading';

const styles = ({
  custom: {
    palette: { colorSupportMidnight },
  },
}) =>
  createStyles({
    checkbox: {
      color: colorSupportMidnight,
    },
  });

class EnhancedTable extends React.PureComponent {
  constructor(props) {
    super(props);
    this.renderRow = this.renderRow.bind(this);
    this.onBeginDrag = this.onBeginDrag.bind(this);
    this.onEndDrag = this.onEndDrag.bind(this);
    this.handleRequestSort = this.handleRequestSort.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.handleClick = this.handleClick.bind(this);

    this.state = {
      order: 'desc',
      orderBy: 'lastUpdated',
      data: null,
      selected: [],
      draggingMode: false,
    };
  }

  componentWillMount() {
    const { order, orderBy } = this.props;

    if (order) this.setState({ order });
    if (orderBy) this.setState({ orderBy });
  }

  componentWillReceiveProps(nextProps) {
    const { data, onClearItems } = this.props;
    if (!isEqual(nextProps.data, data)) {
      this.setState({ data: nextProps.data });
    }

    if (onClearItems) {
      this.setState({ selected: [] });
    }
  }

  isSelected(id) {
    return this.state.selected.indexOf(id) !== -1;
  }

  getColumnData() {
    const { columnData } = this.props;
    const res = [...columnData];
    return res;
  }

  getItems() {
    const { orderBy, order } = this.state;
    const data = this.state.data || this.props.data;
    const columnData = this.getColumnData();
    const column = find(columnData, { id: orderBy });

    // Handle custom sorting logic per column
    if (isFunction(get(column, 'sort'))) {
      return column.sort(data, order);
    }
    // Default sorting logic for dates
    if (get(column, 'date')) {
      return _orderBy(data, [(obj) => parseDate(get(obj, orderBy), 'YYYY-MM-DDTHH:mm:ss.SSS')], [order]);
    }
    return _orderBy(data, orderBy, order);
  }

  handleRequestSort(event, property) {
    const orderBy = property;
    let order = 'desc';
    if (this.state.orderBy === property && this.state.order === 'desc') {
      order = 'asc';
    }
    this.setState({ order, orderBy });
  }

  handleKeyDown(event, id) {
    if (keycode(event) === 'space') {
      this.handleClick(event, id);
    }
  }

  handleClick(item) {
    const { onClick } = this.props;
    onClick(item);
  }

  handleCheckboxChange(event, item, checked) {
    const { onCheck } = this.props;
    onCheck(item, checked);
    const { _id } = item;
    const { selected } = this.state;
    const selectedIndex = selected.indexOf(_id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, _id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(selected.slice(0, selectedIndex), selected.slice(selectedIndex + 1));
    }
    this.setState({ selected: newSelected });
  }

  onBeginDrag() {
    this.setState({ draggingMode: true });
  }

  onEndDrag(id, dropResult) {
    this.setState({ draggingMode: false });
    this.props.onEndDrag(id, dropResult);
  }

  render() {
    const { loading, tableStyle, columnStyle } = this.props;
    const items = this.getItems();
    const { selectable, disableSort } = this.props;
    const { order, orderBy } = this.state;
    const columnData = this.getColumnData();

    return (
      <Table style={{ ...(tableStyle || {}), tableLayout: 'fixed' }}>
        <EnhancedTableHead
          key={columnData.id}
          columnData={columnData}
          columnStyle={columnStyle}
          order={order}
          orderBy={orderBy}
          onRequestSort={this.handleRequestSort}
          selectable={selectable}
          disableSort={disableSort}
        />
        <TableBody style={{ background: 'white' }}>
          {map(items, this.renderRow)}
          {loading && <RowLoading colSpan={size(columnData) + (selectable ? 1 : 0)} />}
        </TableBody>
      </Table>
    );
  }

  renderRow(item, index) {
    const columnData = this.getColumnData();
    const { columnStyle, disableHover, onClick, onEndDrag, onMove, renderActions, renderItem, expanded } = this.props;
    const id = item?.id || item?._id || '-1';

    if (item) {
      return (
        <EnhancedTableRow
          key={index}
          id={id}
          item={item}
          onClick={onClick ? () => this.handleClick(item) : null}
          onKeyDown={(event) => this.handleKeyDown(event, id)}
          onBeginDrag={this.onBeginDrag}
          onEndDrag={onEndDrag ? this.onEndDrag : null}
          onMove={onMove}
          dragging={item.dragging}
          draggingMode={this.state.draggingMode}
          columnData={columnData}
          columnStyle={columnStyle}
          renderActions={renderActions}
          hover={!disableHover}
          expanded={expanded}
          renderItem={renderItem || null}
        >
          {this.renderRowCheckbox(item)}
        </EnhancedTableRow>
      );
    }
  }

  renderRowCheckbox(item) {
    const { selectable } = this.props;
    if (!selectable) {
      return;
    }
    const { classes, itemsSelected, itemsRejected, isSelectAllMarked } = this.props;

    const checked = itemsSelected
      ? itemsSelected === 'all' ||
        (isSelectAllMarked && !includes(itemsRejected, item) && !includes(itemsRejected, item._id)) ||
        includes(itemsSelected, item) ||
        includes(itemsSelected, item._id)
      : this.state.selected.indexOf(item._id) >= 0;

    return (
      <TableCell style={{ paddingLeft: 16 }} checkbox="true">
        <Checkbox
          className={classes.checkbox}
          checked={checked}
          checkedIcon={<CheckBoxIcon className={classes.checkbox} />}
          onClick={(event) => {
            event.stopPropagation();
          }}
          onChange={(event, value) => {
            this.handleCheckboxChange(event, item, value);
            return false;
          }}
        />
      </TableCell>
    );
  }
}

EnhancedTable.propTypes = {
  columnData: PropTypes.array,
  columnStyle: PropTypes.object,
  data: PropTypes.oneOfType([PropTypes.array, PropTypes.bool]),
  disableHover: PropTypes.bool,
  itemsSelected: PropTypes.oneOfType([PropTypes.array, PropTypes.string, PropTypes.bool]),
  itemsRejected: PropTypes.oneOfType([PropTypes.array, PropTypes.string, PropTypes.bool]),
  isSelectAllMarked: PropTypes.bool,
  loading: PropTypes.bool,
  // FIXME: Code is using a boolean, not calling a function
  onClearItems: PropTypes.bool,
  onCheck: PropTypes.func,
  onClick: PropTypes.func,
  onEndDrag: PropTypes.func,
  onMove: PropTypes.func,
  order: PropTypes.string,
  orderBy: PropTypes.string,
  renderActions: PropTypes.oneOfType([PropTypes.func, PropTypes.node]),
  selectable: PropTypes.bool,
  disableSort: PropTypes.bool,
  tableStyle: PropTypes.object,
  renderItem: PropTypes.func,
  expanded: PropTypes.any,
};

EnhancedTable.defaultProps = {
  disableHover: false,
  disableSort: false,
};

export default withStyles(styles, { withTheme: true })(EnhancedTable);
